import { Injectable, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AtomPaginatorIntl } from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { take } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class AtomPaginatorIntlService extends AtomPaginatorIntl {
    private readonly translocoService = inject(TranslocoService);
    private readonly activeLang = toSignal(this.translocoService.langChanges$);

    constructor() {
        super();
        effect(() => {
            this.activeLang();
            this.updateLabels();
        });
    }

    /**
     * Left half of the counter, localized in full: the design system's own
     * implementation hardcodes the English `"{start} - {end} of {length}
     * items"`, so delegating to `super` would leave the common case untranslated.
     *
     * The numbers still go through the design system's own formatters, which
     * are `protected` precisely so a localizing subclass can reuse the compact
     * notation and the locale's thousands separator without inheriting the copy.
     */
    override getItemsCounterLabel(
        pageIndex: number,
        pageSize: number,
        length: number,
        opts: { compact?: boolean } = {},
    ): string {
        if (length === 0) {
            return this.translocoService.translate(
                "atom_paginator.items_counter_empty",
            );
        }

        const compact = opts.compact ?? true;
        const format = (value: number): string =>
            compact ? this.formatCompact(value) : this.formatFull(value);

        const start = pageIndex * pageSize + 1;
        const end = Math.min(length, (pageIndex + 1) * pageSize);

        return this.translocoService.translate(
            "atom_paginator.items_counter",
            {
                start: format(start),
                end: format(end),
                total: format(length),
            },
        );
    }

    override getRangeLabel(
        pageIndex: number,
        pageSize: number,
        length: number,
    ): string {
        if (length === 0) {
            return this.translocoService.translate(
                "atom_paginator.range_label_empty",
            );
        }
        const totalPages = Math.max(1, Math.ceil(length / pageSize));
        return this.translocoService.translate("atom_paginator.range_label", {
            page: pageIndex + 1,
            total: totalPages,
        });
    }

    private updateLabels(): void {
        this.translocoService
            .selectTranslateObject("atom_paginator")
            .pipe(take(1))
            .subscribe((data) => {
                this.paginatorLabel = data.paginator_label;
                this.itemsPerPageLabel = data.items_per_page;
                this.firstPageLabel = data.first_page;
                this.lastPageLabel = data.last_page;
                this.nextPageLabel = data.next_page;
                this.previousPageLabel = data.previous_page;
                this.changes.next();
            });
    }
}
